import Plan from 'types/Plan';
import Typography from '@alltrails/shared/denali/components/Typography';
import { UserId } from '@alltrails/shared/types/User';
import PrimaryLink from 'components/LandingPages/PrimaryLink';
import NoSsr from '@material-ui/core/NoSsr';
import { lazy, Suspense } from 'react';
import chevronDownBouncing from 'components/shared/ChevronDownBouncing/chevron-down-bouncing.svg';
import ProFeatureComparisonTable from 'components/ProFeatureComparisonTable';
import useUser from 'hooks/useUser';
import usePlusFeatures from 'components/LandingPages/hooks/usePlusFeatures';
import Coupon from 'types/Coupon';
import { MEDIA_QUERY_SMALL_DOWN } from 'utils/constants/breakpoints';
import heroDesktopJpg from './images/hero-2880w.jpg';
import heroDesktopWebp from './images/hero-2880w.webp';
import heroDesktopAvif from './images/hero-2880w.avif';
import logosDesktopPng from './images/logosDesktop.png';
import logosDesktopWebp from './images/logosDesktop.webp';
import logosDesktopAvif from './images/logosDesktop.avif';
import logosMobilePng from './images/logosMobile.png';
import logosMobileWebp from './images/logosMobile.webp';
import logosMobileAvif from './images/logosMobile.avif';
import * as styles from './KathmanduLandingPage.module.scss';

// No designs, but similar to: https://www.figma.com/file/P4IkeqfAVc6FYxlyPS7Yov/F-2023_Partnerships?type=design&node-id=2449-97806&t=QuZyph2Uqqp4c2Io-0

const ChevronDownBouncing = lazy(() => import('components/shared/ChevronDownBouncing'));

type Props = {
  coupon?: Coupon;
  plan: Plan;
  userId?: UserId;
};

function getTrialMonths(coupon: Coupon, trialPreview: string): string {
  const couponTrial = coupon?.freeTrialDuration;
  if (coupon) {
    const durationData = couponTrial.split(' ');
    return durationData[0];
  }

  if (trialPreview === '3months') {
    return '3';
  }

  if (trialPreview === '6months') {
    return '6';
  }

  if (trialPreview === '12months') {
    return '12';
  }

  return '';
}

export default function KathmanduLandingPage({ plan, userId, coupon }: Props) {
  const user = useUser();

  const params = new URLSearchParams(__AT_DATA__.search);

  if (coupon) {
    params.set('k', coupon.code);
  }

  if (userId) {
    params.set('uid', String(userId));
  }

  const trialParam = params.get('trial');

  const trialMonths = getTrialMonths(coupon, trialParam);

  const search = params.toString();
  const to = `${user || userId ? '/plus/checkout' : '/signup'}${search.length > 0 ? `?${search}` : ''}`;

  const features = usePlusFeatures();

  return (
    <div className={styles.wrapper}>
      <div className={styles.hero}>
        <picture className={styles.picture}>
          <source srcSet={heroDesktopAvif} type="image/avif" />
          <source srcSet={heroDesktopWebp} type="image/webp" />
          <img alt="A hiker is walking along a trail with mountains in the background." src={heroDesktopJpg} />
        </picture>
      </div>
      <div className={styles.content}>
        <div className={styles.copy}>
          <div>
            <picture>
              <source srcSet={logosMobileAvif} type="image/avif" media={MEDIA_QUERY_SMALL_DOWN} />
              <source srcSet={logosMobileWebp} type="image/webp" media={MEDIA_QUERY_SMALL_DOWN} />
              <source srcSet={logosMobilePng} type="image/png" media={MEDIA_QUERY_SMALL_DOWN} />
              <source srcSet={logosDesktopAvif} type="image/avif" />
              <source srcSet={logosDesktopWebp} type="image/webp" />
              <img src={logosDesktopPng} alt="AllTrails + OutThere logos" className={styles.logo} />
            </picture>
          </div>
          <Typography className={styles.subLogo} variant="text300bold">
            Out There Rewards member exclusive
          </Typography>
          <Typography className={styles.title} component="p">{`Get ${trialMonths} Months of AllTrails+`}</Typography>
          <Typography className={styles.subhead} component="p">
            Find more places to explore and enjoy with a free AllTrails+ membership. Access over 420,000 trails and get ready to escape into nature!
          </Typography>
          <div className={styles.button}>
            <PrimaryLink buttonText="Redeem Now" plan={plan} coupon={coupon} to={to} desktopVariant="elevated" mobileVariant="primary" />
            <Typography className={styles.cancel} component="div" mb="8" variant="text100">
              <p>Cancel anytime.</p>
            </Typography>
          </div>
          <Typography className={styles.lottie} component="div" mb="8">
            <NoSsr>
              <Suspense fallback={<img alt="" src={chevronDownBouncing} />}>
                <ChevronDownBouncing />
              </Suspense>
            </NoSsr>
          </Typography>
        </div>
        <ProFeatureComparisonTable features={features} />
      </div>
    </div>
  );
}
